import React, { useState } from "react";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  initiateEmailChange,
  isExplicitlyImpersonating,
} from "../auth/emailChange";
import { useTranslation } from "../context/Internationalization";
import { Modal, ModalActions } from "./Modal";
import ModalBody from "./Modal/ModalBody";
import ModalTitle from "./Modal/ModalTitle";

const readableErrorKey = (error) => {
  const value = `${error?.code || ""} ${error?.message || ""}`.toLowerCase();
  if (value.includes("impersonat"))
    return "email_change_impersonation_forbidden";
  if (
    value.includes("already-in-use") ||
    value.includes("already in use") ||
    value.includes("already exists") ||
    value.includes("user_already_exists") ||
    value.includes("auth_identity_conflict")
  )
    return "email_change_already_in_use";
  if (value.includes("invalid_email")) return "invalid_email";
  if (value.includes("auth_required") || value.includes("user_not_found"))
    return "email_change_auth_required";
  return "email_change_send_failed";
};

const SmsLoginEmailPromptModal = ({
  open = false,
  onClose = () => {},
  user,
  language,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const impersonating = isExplicitlyImpersonating();

  const close = () => {
    if (status === "pending") return;
    setStatus("idle");
    setError("");
    setEmail("");
    onClose();
  };
  const remindLater = () => {
    localStorage.setItem(
      "emailPromptRemindAfter",
      String(Date.now() + 24 * 60 * 60 * 1000)
    );
    close();
  };
  const submit = async (event) => {
    event.preventDefault();
    if (status === "pending" || impersonating) return;
    setStatus("pending");
    setError("");
    try {
      await initiateEmailChange({ email, language });
      setStatus("sent");
    } catch (submitError) {
      setError(t(readableErrorKey(submitError)));
      setStatus("idle");
    }
  };

  const title = user?.email ? t("change_email") : t("add_email_title");
  const verificationSentMessage = user?.email
    ? t("email_change_verification_sent")
    : t("email_add_verification_sent");

  return (
    <Modal
      open={open}
      onClose={close}
      type="info"
      ariaLabel={title}
      closeLabel={t("close")}
    >
      <form onSubmit={submit}>
        <ModalTitle title={title} />
        <ModalBody>
          <Box sx={{ width: "100%" }}>
            {status === "sent" ? (
              <Alert severity="success">{verificationSentMessage}</Alert>
            ) : (
              <>
                {user?.email && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t("current_email")}: {user.email}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("email_change_verification_notice")}
                </Typography>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                {impersonating && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {t("email_change_impersonation_forbidden")}
                  </Alert>
                )}
                <TextField
                  autoFocus
                  fullWidth
                  required
                  type="email"
                  label={user?.email ? t("new_email") : t("email")}
                  value={email}
                  disabled={status === "pending" || impersonating}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </>
            )}
          </Box>
        </ModalBody>
        <ModalActions>
          {!user?.email && status !== "sent" && (
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={remindLater}
              disabled={status === "pending"}
            >
              {t("remind_me_later")}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={close}
          >
            {status === "sent" ? t("close") : t("cancel")}
          </Button>
          {status !== "sent" && (
            <LoadingButton
              startIcon={<SendOutlinedIcon />}
              loadingPosition="start"
              type="submit"
              fullWidth
              loading={status === "pending"}
              disabled={impersonating}
            >
              {t("send_verification_link")}
            </LoadingButton>
          )}
        </ModalActions>
      </form>
    </Modal>
  );
};

export default SmsLoginEmailPromptModal;
